import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DisplayContainer from "../../../../components/displayContainer/DisplayContainer";
import Loader from "../../../../components/loader/Loader";
import PrimeBadge from "../../../../components/primeBadge/PrimeBadge";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import Button from "../../../../components/button/Button";
import {
  fetchPreviousReports,
  fetchCommentReports,
  setSelectedComment,
  setSelectedTournament,
  setStatusFilter,
  setActionFilter,
} from "../../../../features/tournamentCommentReports/tournamentCommentReportsSlice";
import { showConfirmationModal } from "../../../../features/confirmationModal/ConfirmationModalSlice";
import styles from "./previousReports.module.css";

const REASON_LABELS = {
  spam: "إعلان مزعج",
  harassment: "مضايقة",
  hate: "كراهية",
  nudity: "محتوى غير لائق",
  violence: "عنف",
  misinformation: "معلومات خاطئة",
  selfHarm: "إيذاء النفس أو أفكار انتحارية",
  other: "أخرى",
};

const ACTION_LABELS = {
  hidden: "مخفي",
  deleted: "محذوف",
  dismissed: "مرفوض",
};

const PreviousReports = () => {
  const dispatch = useDispatch();
  const {
    previousReports,
    selectedComment,
    commentReports,
    tournaments,
    previousReportsPage,
    previousReportsHasMore,
    fetchPreviousReportsStatus,
    actionStatus,
    selectedTournamentId,
    selectedStatusFilter,
    selectedActionFilter,
  } = useSelector((state) => state.tournamentCommentReports);
  const { rolePermissions } = useSelector((state) => state.user);
  const can = (permission) => rolePermissions.includes(permission);

  const commentsListRef = useRef();
  const [showReports, setShowReports] = useState(false);
  const prevActionStatus = useRef(actionStatus);

  // Fetch initial data
  useEffect(() => {
    if (fetchPreviousReportsStatus === "idle") {
      dispatch(
        fetchPreviousReports({
          status: null, // Get both resolved and dismissed
          page: 1,
        }),
      );
    }
  }, [dispatch, fetchPreviousReportsStatus]);

  // Refresh list after successful actions
  useEffect(() => {
    if (
      prevActionStatus.current === "loading" &&
      actionStatus === "succeeded"
    ) {
      dispatch(
        fetchPreviousReports({
          status: selectedStatusFilter,
          tournamentId: selectedTournamentId,
          actionTaken: selectedActionFilter,
          page: 1,
        }),
      );
    }
    prevActionStatus.current = actionStatus;
  }, [dispatch, actionStatus, selectedTournamentId, selectedStatusFilter, selectedActionFilter]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = commentsListRef.current;
    if (
      !container ||
      fetchPreviousReportsStatus === "loading" ||
      !previousReportsHasMore
    )
      return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      previousReportsHasMore &&
      previousReportsPage
    ) {
      dispatch(
        fetchPreviousReports({
          status: selectedStatusFilter,
          tournamentId: selectedTournamentId,
          actionTaken: selectedActionFilter,
          page: previousReportsPage + 1,
        }),
      );
    }
  }, [
    dispatch,
    fetchPreviousReportsStatus,
    previousReportsHasMore,
    previousReportsPage,
    selectedTournamentId,
    selectedStatusFilter,
    selectedActionFilter,
  ]);

  useEffect(() => {
    const container = commentsListRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  // Handle tournament selection
  const handleTournamentSelect = (tournamentId) => {
    dispatch(setSelectedTournament(tournamentId));
    dispatch(
      fetchPreviousReports({
        status: selectedStatusFilter,
        tournamentId,
        actionTaken: selectedActionFilter,
        page: 1,
      }),
    );
  };

  // Handle status filter
  const handleStatusFilterChange = (status) => {
    dispatch(setStatusFilter(status));
    dispatch(
      fetchPreviousReports({
        status,
        tournamentId: selectedTournamentId,
        actionTaken: selectedActionFilter,
        page: 1,
      }),
    );
  };

  // Handle action filter
  const handleActionFilterChange = (actionTaken) => {
    dispatch(setActionFilter(actionTaken));
    dispatch(
      fetchPreviousReports({
        status: selectedStatusFilter,
        tournamentId: selectedTournamentId,
        actionTaken,
        page: 1,
      }),
    );
  };

  // Handle comment selection
  const handleCommentSelect = async (comment) => {
    dispatch(setSelectedComment(comment));
    setShowReports(false);
    await dispatch(
      fetchCommentReports(comment.commentId || comment.comment?._id),
    );
    setShowReports(true);
  };

  // Handle unhide comment
  const handleUnhideComment = () => {
    if (!selectedComment) return;
    const commentId = selectedComment.commentId || selectedComment.comment?._id;
    dispatch(
      showConfirmationModal({
        message: "هل أنت متأكد من إظهار هذا التعليق؟",
        actionType: "unhideComment",
        data: { commentId, isHidden: false },
      }),
    );
  };

  // Handle restore deleted comment
  const handleRestoreComment = () => {
    if (!selectedComment) return;
    const commentId = selectedComment.commentId || selectedComment.comment?._id;
    dispatch(
      showConfirmationModal({
        message: "هل أنت متأكد من استعادة هذا التعليق؟",
        actionType: "restoreComment",
        data: { commentId },
      }),
    );
  };

  // Handle delete comment
  const handleDeleteComment = () => {
    if (!selectedComment) return;
    const commentId = selectedComment.commentId || selectedComment.comment?._id;
    dispatch(
      showConfirmationModal({
        message:
          "هل أنت متأكد من حذف هذا التعليق؟ سيتم حذف التعليق والردود عليه.",
        actionType: "deleteComment",
        data: { commentId },
      }),
    );
  };

  // Handle hide comment
  const handleHideComment = () => {
    if (!selectedComment) return;
    const commentId = selectedComment.commentId || selectedComment.comment?._id;
    dispatch(
      showConfirmationModal({
        message: "هل أنت متأكد من إخفاء هذا التعليق؟",
        actionType: "hideComment",
        data: { commentId, isHidden: true },
      }),
    );
  };

  // Get total reports count for "All Tournaments"
  const allTournamentsReportsCount = previousReports.reduce(
    (sum, item) => sum + (item.reportsCount || 0),
    0,
  );

  return (
    <div className={styles.container}>
      <TextWrapper textStyle="textTitle">التقارير السابقة</TextWrapper>
      <div className={styles.displayContainerWrapper}>
        <DisplayContainer>
          <div className={styles.reportsWrapper}>
            {/* Left Side - Filters, Tournaments and Comments List */}
            <div className={styles.leftSide}>
              {/* Filters */}
              <div className={styles.filtersSection}>
                <TextWrapper textStyle="textMid">الفلاتر</TextWrapper>
                
                {/* Status Filter */}
                <div className={styles.filterGroup}>
                  <TextWrapper textStyle="textSmall">حالة التقرير:</TextWrapper>
                  <div className={styles.filterButtons}>
                    <button
                      className={`${styles.filterButton} ${
                        selectedStatusFilter === null ? styles.active : ""
                      }`}
                      onClick={() => handleStatusFilterChange(null)}
                    >
                      <TextWrapper textStyle="textXSmall">الكل</TextWrapper>
                    </button>
                    <button
                      className={`${styles.filterButton} ${
                        selectedStatusFilter === "resolved" ? styles.active : ""
                      }`}
                      onClick={() => handleStatusFilterChange("resolved")}
                    >
                      <TextWrapper textStyle="textXSmall">تم الحل</TextWrapper>
                    </button>
                    <button
                      className={`${styles.filterButton} ${
                        selectedStatusFilter === "dismissed" ? styles.active : ""
                      }`}
                      onClick={() => handleStatusFilterChange("dismissed")}
                    >
                      <TextWrapper textStyle="textXSmall">مرفوض</TextWrapper>
                    </button>
                  </div>
                </div>

                {/* Action Filter */}
                <div className={styles.filterGroup}>
                  <TextWrapper textStyle="textSmall">الإجراء المتخذ:</TextWrapper>
                  <div className={styles.filterButtons}>
                    <button
                      className={`${styles.filterButton} ${
                        selectedActionFilter === null ? styles.active : ""
                      }`}
                      onClick={() => handleActionFilterChange(null)}
                    >
                      <TextWrapper textStyle="textXSmall">الكل</TextWrapper>
                    </button>
                    <button
                      className={`${styles.filterButton} ${
                        selectedActionFilter === "hidden" ? styles.active : ""
                      }`}
                      onClick={() => handleActionFilterChange("hidden")}
                    >
                      <TextWrapper textStyle="textXSmall">مخفي</TextWrapper>
                    </button>
                    <button
                      className={`${styles.filterButton} ${
                        selectedActionFilter === "deleted" ? styles.active : ""
                      }`}
                      onClick={() => handleActionFilterChange("deleted")}
                    >
                      <TextWrapper textStyle="textXSmall">محذوف</TextWrapper>
                    </button>
                    <button
                      className={`${styles.filterButton} ${
                        selectedActionFilter === "dismissed" ? styles.active : ""
                      }`}
                      onClick={() => handleActionFilterChange("dismissed")}
                    >
                      <TextWrapper textStyle="textXSmall">مرفوض</TextWrapper>
                    </button>
                  </div>
                </div>
              </div>

              {/* Tournaments List */}
              <div className={styles.tournamentsList}>
                <TextWrapper textStyle="textMid">البطولات</TextWrapper>
                <div
                  className={`${styles.tournamentItem} ${
                    selectedTournamentId === null ? styles.active : ""
                  }`}
                  onClick={() => handleTournamentSelect(null)}
                >
                  <TextWrapper textStyle="textSmall">جميع البطولات</TextWrapper>
                  <TextWrapper textStyle="textXSmall">
                    ({allTournamentsReportsCount} تقرير)
                  </TextWrapper>
                </div>
                {tournaments.map((tournament) => (
                  <div
                    key={tournament.tournamentId}
                    className={`${styles.tournamentItem} ${
                      selectedTournamentId === tournament.tournamentId
                        ? styles.active
                        : ""
                    }`}
                    onClick={() =>
                      handleTournamentSelect(tournament.tournamentId)
                    }
                  >
                    <TextWrapper textStyle="textSmall">
                      {tournament.name ||
                        `بطولة ${tournament.tournamentId.slice(0, 8)}...`}
                    </TextWrapper>
                    <TextWrapper textStyle="textXSmall">
                      ({tournament.reportsCount} تقرير)
                    </TextWrapper>
                  </div>
                ))}
              </div>

              {/* Comments List */}
              <div className={styles.commentsList} ref={commentsListRef}>
                <TextWrapper textStyle="textMid">التعليقات</TextWrapper>
                {previousReports.map((item) => {
                  const commentId = item.commentId || item.comment?._id;
                  const isSelected =
                    selectedComment?.commentId === commentId ||
                    selectedComment?.comment?._id === commentId;

                  return (
                    <div
                      key={commentId}
                      className={`${styles.commentItem} ${
                        isSelected ? styles.active : ""
                      }`}
                      onClick={() => handleCommentSelect(item)}
                    >
                      <TextWrapper textStyle="textSmall">
                        {item.comment?.text?.substring(0, 50) ||
                          "تعليق بدون نص"}
                        {item.comment?.text?.length > 50 ? "..." : ""}
                      </TextWrapper>
                      <TextWrapper textStyle="textXSmall">
                        {item.reportsCount} تقرير
                      </TextWrapper>
                      {item.actionTaken && (
                        <div className={styles.actionTag}>
                          <TextWrapper textStyle="textXSmall">
                            ({ACTION_LABELS[item.actionTaken] || item.actionTaken})
                          </TextWrapper>
                        </div>
                      )}
                      {item.comment?.isDeleted && (
                        <div className={styles.deletedStatus}>
                          <TextWrapper textStyle="textXSmall">
                            (محذوف)
                          </TextWrapper>
                        </div>
                      )}
                      {item.comment?.isHidden && (
                        <div className={styles.hiddenStatus}>
                          <TextWrapper textStyle="textXSmall">
                            (مخفي)
                          </TextWrapper>
                        </div>
                      )}
                    </div>
                  );
                })}
                {fetchPreviousReportsStatus === "loading" && (
                  <div className={styles.loaderContainer}>
                    <Loader size={30} color="var(--color-text-primary)" />
                  </div>
                )}
                {previousReports.length === 0 &&
                  fetchPreviousReportsStatus === "succeeded" && (
                    <TextWrapper textStyle="textMid">
                      لا توجد تقارير سابقة
                    </TextWrapper>
                  )}
              </div>
            </div>

            {/* Right Side - Comment Details */}
            <div className={styles.rightSide}>
              {!selectedComment ? (
                <div className={styles.emptyState}>
                  <TextWrapper textStyle="textTitle">
                    اختر تعليقاً لعرض التفاصيل
                  </TextWrapper>
                </div>
              ) : (
                <div className={styles.commentDetails}>
                  {/* Comment Text */}
                  <div className={styles.commentTextSection}>
                    <TextWrapper textStyle="textMid">نص التعليق:</TextWrapper>
                    <div className={styles.commentText}>
                      <TextWrapper textStyle="textSmall">
                        {selectedComment.comment?.text || "لا يوجد نص"}
                      </TextWrapper>
                    </div>
                  </div>

                  {/* Comment Author */}
                  <div className={styles.commentAuthorSection}>
                    <TextWrapper textStyle="textMid">المستخدم:</TextWrapper>
                    <TextWrapper textStyle="textSmall">
                      {selectedComment.commentUser?.name ||
                        selectedComment.commentUser?.userName ||
                        "مستخدم غير معروف"}
                    </TextWrapper>
                    <PrimeBadge
                      isPrime={selectedComment.commentUser?.isPrime}
                      primeTier={selectedComment.commentUser?.primeTier}
                      size="small"
                    />
                  </div>

                  {/* Action Taken */}
                  {selectedComment.actionTaken && (
                    <div className={styles.actionTakenSection}>
                      <TextWrapper textStyle="textMid">الإجراء المتخذ:</TextWrapper>
                      <TextWrapper textStyle="textSmall">
                        {ACTION_LABELS[selectedComment.actionTaken] ||
                          selectedComment.actionTaken}
                      </TextWrapper>
                    </div>
                  )}

                  {/* Reports Summary */}
                  <div className={styles.reportsSummarySection}>
                    <TextWrapper textStyle="textMid">
                      عدد التقارير: {selectedComment.reportsCount || 0}
                    </TextWrapper>
                    {selectedComment.reasons &&
                      selectedComment.reasons.length > 0 && (
                        <div className={styles.reasonsList}>
                          <TextWrapper textStyle="textSmall">
                            أسباب الإبلاغ:
                          </TextWrapper>
                          {selectedComment.reasons.map((reason, index) => (
                            <TextWrapper key={index} textStyle="textXSmall">
                              - {REASON_LABELS[reason] || reason}
                            </TextWrapper>
                          ))}
                        </div>
                      )}
                  </div>

                  {/* Detailed Reports */}
                  {showReports && commentReports.length > 0 && (
                    <div className={styles.detailedReportsSection}>
                      <TextWrapper textStyle="textMid">
                        تفاصيل التقارير:
                      </TextWrapper>
                      <div className={styles.reportsList}>
                        {commentReports.map((report, index) => (
                          <div
                            key={report._id || index}
                            className={styles.reportItem}
                          >
                            <TextWrapper textStyle="textSmall">
                              <strong>المبلغ:</strong>{" "}
                              {report.reporterId?.name ||
                                report.reporterId?.userName ||
                                "مستخدم غير معروف"}
                              <PrimeBadge
                                isPrime={report.reporterId?.isPrime}
                                primeTier={report.reporterId?.primeTier}
                                size="small"
                              />
                            </TextWrapper>
                            <TextWrapper textStyle="textXSmall">
                              <strong>السبب:</strong>{" "}
                              {REASON_LABELS[report.reason] || report.reason}
                            </TextWrapper>
                            {report.note && (
                              <TextWrapper textStyle="textXSmall">
                                <strong>ملاحظة:</strong> {report.note}
                              </TextWrapper>
                            )}
                            <TextWrapper textStyle="textXSmall">
                              <strong>التاريخ:</strong>{" "}
                              {new Date(report.createdAt).toLocaleDateString(
                                "ar-SA",
                              )}
                            </TextWrapper>
                            <TextWrapper textStyle="textXSmall">
                              <strong>الحالة:</strong>{" "}
                              {report.status === "open"
                                ? "مفتوح"
                                : report.status === "resolved"
                                  ? "تم الحل"
                                  : "مرفوض"}
                            </TextWrapper>
                            {report.actionTaken && (
                              <TextWrapper textStyle="textXSmall">
                                <strong>الإجراء:</strong>{" "}
                                {ACTION_LABELS[report.actionTaken] ||
                                  report.actionTaken}
                              </TextWrapper>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className={styles.actionsSection}>
                    {can("hide-comments") && (selectedComment.comment?.isHidden ? (
                      <Button
                        variant="greenVariant"
                        size="midSize"
                        functionallity={handleUnhideComment}
                        disabled={actionStatus === "loading"}
                      >
                        <TextWrapper textStyle="textSmall">
                          إظهار التعليق
                        </TextWrapper>
                      </Button>
                    ) : (
                      <Button
                        variant="blackVariant"
                        size="midSize"
                        functionallity={handleHideComment}
                        disabled={actionStatus === "loading"}
                      >
                        <TextWrapper textStyle="textSmall">
                          إخفاء التعليق
                        </TextWrapper>
                      </Button>
                    ))}
                    {selectedComment.comment?.isDeleted ? (
                      can("restore-comments") && (
                      <Button
                        variant="greenVariant"
                        size="midSize"
                        functionallity={handleRestoreComment}
                        disabled={actionStatus === "loading"}
                      >
                        <TextWrapper textStyle="textSmall">
                          استعادة التعليق
                        </TextWrapper>
                      </Button>
                      )
                    ) : (
                      can("delete-comments") && (
                      <Button
                        variant="redVariant"
                        size="midSize"
                        functionallity={handleDeleteComment}
                        disabled={actionStatus === "loading"}
                      >
                        <TextWrapper textStyle="textSmall">
                          حذف التعليق
                        </TextWrapper>
                      </Button>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DisplayContainer>
      </div>
    </div>
  );
};

export default PreviousReports;
